import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import * as d3 from 'd3';
import { DEMO_PEDIGREE } from '../../data/demoPedigree';
import { getTranslatedTerm } from '../../data/kinshipTranslations';
import { getCardBg, getCardBorder, getAvatarBg, getAvatarTextColor, getInitials } from '../../utils/treeHelpers';
import { CARD_W, CARD_H, GAP_X, GAP_COUPLE, LEVEL_GAP, drawPersonCard, drawCoupleConnector } from '../../utils/treeDrawing';

const FamilyTree = forwardRef(function FamilyTree({ pedigreeData, onNodeClick, onViewFamily, query, selectedLanguage, activePersonId }, ref) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  // Sync: ensure all Level -1 groups start collapsed (pedigreeData may be null on first render)
  useEffect(() => {
    if (!pedigreeData) return;
    const { rootChildren = [], rootSiblings = [], rootGeneration } = pedigreeData;
    const generationRowInit = rootGeneration || [...rootSiblings, pedigreeData.root];
    const totalGroups = (rootChildren.length > 0 ? 1 : 0) + generationRowInit.filter(s => s.children && s.children.length > 0).length;
    if (totalGroups > 0 && collapsedGroups.size === 0) {
      const allIds = new Set();
      for (let i = 0; i < totalGroups; i++) allIds.add(`lminus1-${i}`);
      setCollapsedGroups(allIds);
    }
  }, [pedigreeData]);
  const zoomRef = useRef(null);
  const initialRef = useRef(null);
  const savedTransform = useRef(null); // preserve zoom/pan across re-renders

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);



  useEffect(() => {
    if (!containerRef.current || !pedigreeData) return;

    // Save current zoom/pan state before redrawing
    const currentSvg = d3.select(containerRef.current).select('svg');
    if (!currentSvg.empty() && zoomRef.current) {
      try {
        const currentTransform = d3.zoomTransform(currentSvg.node());
        if (currentTransform) savedTransform.current = currentTransform;
      } catch (e) { /* ignore */ }
    }
    currentSvg.remove();

    const width = dimensions.width;
    const height = dimensions.height;
    const { root, rootSiblings = [], rootGeneration, rootChildren = [], parentGeneration, grandparentGeneration } = pedigreeData;
    const { father, mother, fatherSiblings = [], motherSiblings = [], stepFather, stepMother, stepFatherSiblings = [], stepMotherSiblings = [], paternalHalfSiblings = [], maternalHalfSiblings = [] } = parentGeneration;

    // Use rootGeneration (age-sorted including root person) if available
    const generationRow = rootGeneration || [...rootSiblings, root];
    const isRootById = root?.id;

    // Layout constants from treeDrawing.js

    const level2Y = CARD_H / 2 + 20;
    const level1Y = level2Y + CARD_H + LEVEL_GAP;
    const level0Y = level1Y + CARD_H + LEVEL_GAP;
    const hasLevelMinus1 = rootChildren.length > 0 || generationRow.some(s => s.children && s.children.length > 0);
    const anyLevelMinus1Expanded = hasLevelMinus1 && collapsedGroups.size < (rootChildren.length > 0 ? 1 : 0) + generationRow.filter(s => s.children && s.children.length > 0).length;
    const levelMinus1Y = anyLevelMinus1Expanded ? level0Y + CARD_H + LEVEL_GAP : level0Y;

    function childrenWidth(children) {
      // Width needed for N children cards side by side
      if (!children || children.length === 0) return 0;
      return children.length * CARD_W + (children.length - 1) * GAP_X;
    }

    function coupleWidth(sib) {
      return sib.spouse ? 2 * CARD_W + GAP_COUPLE : CARD_W;
    }

    function siblingUnitWidth(sib) {
      const selfW = coupleWidth(sib);
      const childW = childrenWidth(sib.children);
      return Math.max(selfW, childW);
    }

    // ════════════════════════════════════════════════════════════════
    // BOTTOM-UP LAYOUT: compute Level 0 first, then Level 1, then Level 2
    // This prevents cousin/sibling overlap by treating Level 0 as one group
    // ════════════════════════════════════════════════════════════════

    // ── Step 1: Compute Level 0 groups (left-to-right) ──
    // Level 0 groups: [father-side cousins...] [root + root siblings] [mother-side cousins...]
    // Each cousin group is centered under its parent couple

    const fatherSibData = [];
    fatherSiblings.forEach((sib) => {
      const children = sib.children || [];
      const childData = children.map((child, ci) => ({ child, childX: 0 }));
      fatherSibData.push({ sib, sibX: 0, spouse: sib.spouse, spouseX: null, childData, parentMidX: 0 });
    });
    // Add step-father's siblings to father-side (paternal aunts/uncles by marriage)
    stepFatherSiblings.forEach((sib) => {
      const children = sib.children || [];
      const childData = children.map((child, ci) => ({ child, childX: 0 }));
      fatherSibData.push({ sib, sibX: 0, spouse: sib.spouse, spouseX: null, childData, parentMidX: 0 });
    });

    const motherSibData = [];
    motherSiblings.forEach((sib) => {
      const children = sib.children || [];
      const childData = children.map((child, ci) => ({ child, childX: 0 }));
      motherSibData.push({ sib, sibX: 0, spouse: sib.spouse, spouseX: null, childData, parentMidX: 0 });
    });
    // Add step-mother's siblings to mother-side (maternal aunts/uncles by marriage)
    stepMotherSiblings.forEach((sib) => {
      const children = sib.children || [];
      const childData = children.map((child, ci) => ({ child, childX: 0 }));
      motherSibData.push({ sib, sibX: 0, spouse: sib.spouse, spouseX: null, childData, parentMidX: 0 });
    });

    const genRowData = generationRow.map((person) => ({ person, sibX: 0, spouseX: person.spouse ? 0 : null, childData: (person.children || []).map(c => ({ child: c, childX: 0 })), parentMidX: 0, isRoot: person.isRoot || person.id === isRootById }));

    // Compute group widths for Level 0
    // Father-side: each aunt/uncle's children form a group
    const fatherCousinGroups = fatherSibData.filter(d => d.childData.length > 0);
    const fatherCousinGroupWidths = fatherCousinGroups.map(d => childrenWidth(d.sib.children));
    // Mother-side
    const motherCousinGroups = motherSibData.filter(d => d.childData.length > 0);
    const motherCousinGroupWidths = motherCousinGroups.map(d => childrenWidth(d.sib.children));

    // Half-sibling groups (paternal half-siblings from father+step-mother, maternal from mother+step-father)
    const paternalHalfData = (paternalHalfSiblings || []).map(p => ({ person: p, childX: 0 }));
    const maternalHalfData = (maternalHalfSiblings || []).map(p => ({ person: p, childX: 0 }));
    const paternalHalfW = paternalHalfData.length * CARD_W + (paternalHalfData.length > 1 ? (paternalHalfData.length - 1) * GAP_X : 0);
    const maternalHalfW = maternalHalfData.length * CARD_W + (maternalHalfData.length > 1 ? (maternalHalfData.length - 1) * GAP_X : 0);

    // Root group: root + siblings
    const rootGroupW = generationRow.length * CARD_W + (generationRow.length - 1) * GAP_X
      + generationRow.reduce((sum, s) => sum + (s.spouse ? CARD_W + GAP_COUPLE : 0), 0);

    // Place all Level 0 groups left-to-right with gaps between groups
    let cursor = 0;
    const level0GroupPositions = []; // { type, index, startX, endX }

    // Father-side cousin groups
    fatherCousinGroups.forEach((d, gi) => {
      const gw = fatherCousinGroupWidths[gi];
      level0GroupPositions.push({ type: 'fcousin', index: gi, startX: cursor, endX: cursor + gw, width: gw });
      cursor += gw + GAP_X;
    });

    // Paternal half-siblings
    if (paternalHalfData.length > 0) {
      level0GroupPositions.push({ type: 'phalf', index: 0, startX: cursor, endX: cursor + paternalHalfW, width: paternalHalfW });
      cursor += paternalHalfW + GAP_X;
    }

    // Root group
    level0GroupPositions.push({ type: 'root', index: -1, startX: cursor, endX: cursor + rootGroupW, width: rootGroupW });
    const rootGroupStartX = cursor;
    cursor += rootGroupW + GAP_X;

    // Maternal half-siblings
    if (maternalHalfData.length > 0) {
      level0GroupPositions.push({ type: 'mhalf', index: 0, startX: cursor, endX: cursor + maternalHalfW, width: maternalHalfW });
      cursor += maternalHalfW + GAP_X;
    }

    // Mother-side cousin groups
    motherCousinGroups.forEach((d, gi) => {
      const gw = motherCousinGroupWidths[gi];
      level0GroupPositions.push({ type: 'mcousin', index: gi, startX: cursor, endX: cursor + gw, width: gw });
      cursor += gw + GAP_X;
    });

    // Calculate shift so the root person is centered
    let rootSibPreShiftWidth = 0;
    let rootFound = false;
    for (const p of generationRow) {
      if (p.isRoot || p.id === isRootById) {
        rootFound = true;
        // Add half card width to get to the center of root person
        rootSibPreShiftWidth += CARD_W / 2;
        break;
      }
      rootSibPreShiftWidth += CARD_W + GAP_X;
      if (p.spouse) rootSibPreShiftWidth += GAP_COUPLE + CARD_W;
    }
    if (!rootFound) rootSibPreShiftWidth = 0;
    const rootPreShiftX = rootGroupStartX + rootSibPreShiftWidth;
    const shiftX = width / 2 - rootPreShiftX;

    // ── Step 2: Assign Level 0 positions ──
    const fatherCousinData = [];
    let fcGroupIdx = 0;
    level0GroupPositions.filter(g => g.type === 'fcousin').forEach((group) => {
      const d = fatherCousinGroups[group.index];
      const startX = group.startX + shiftX;
      d.childData.forEach((cd, ci) => {
        cd.childX = startX + ci * (CARD_W + GAP_X) + CARD_W / 2;
        fatherCousinData.push({ child: cd.child, childX: cd.childX, parentMidX: cd.childX });
      });
      fcGroupIdx++;
    });
    // Root generation: lay out all persons in age order (including root)
    let rootSibCursorX = rootGroupStartX + shiftX;
    let centerX = 0;
    let rootSpouseX = null;
    genRowData.forEach((d, i) => {
      d.sibX = rootSibCursorX + CARD_W / 2;
      rootSibCursorX += CARD_W;
      if (d.person.spouse) {
        d.spouseX = rootSibCursorX + GAP_COUPLE + CARD_W / 2;
        rootSibCursorX += GAP_COUPLE + CARD_W;
        d.parentMidX = (d.sibX + d.spouseX) / 2;
      } else {
        d.parentMidX = d.sibX;
      }
      if (d.isRoot) {
        centerX = d.sibX;
        rootSpouseX = d.spouseX;
      }
      rootSibCursorX += GAP_X;
    });

    // Mother-side cousin groups
    const motherCousinData = [];
    level0GroupPositions.filter(g => g.type === 'mcousin').forEach((group) => {
      const d = motherCousinGroups[group.index];
      const startX = group.startX + shiftX;
      d.childData.forEach((cd, ci) => {
        cd.childX = startX + ci * (CARD_W + GAP_X) + CARD_W / 2;
        motherCousinData.push({ child: cd.child, childX: cd.childX, parentMidX: cd.childX });
      });
    });

    // Paternal half-sibling positions
    const paternalHalfPositions = level0GroupPositions.filter(g => g.type === 'phalf');
    paternalHalfData.forEach((p, i) => {
      p.childX = paternalHalfPositions.length > 0 ? paternalHalfPositions[0].startX + shiftX + i * (CARD_W + GAP_X) + CARD_W / 2 : 0;
    });

    // Maternal half-sibling positions
    const maternalHalfPositions = level0GroupPositions.filter(g => g.type === 'mhalf');
    maternalHalfData.forEach((p, i) => {
      p.childX = maternalHalfPositions.length > 0 ? maternalHalfPositions[0].startX + shiftX + i * (CARD_W + GAP_X) + CARD_W / 2 : 0;
    });

    // ── Step 3: Position Level 1 (parents + step-parents + aunts/uncles) centered above their Level 0 children ──
    // If mother has a step-father, show mother+stepFather as couple; if father has step-mother, show father+stepMother.
    // Otherwise show father+mother as couple.
    let fatherX, motherX, stepFatherX = null, stepMotherX = null;
    let fatherCoupleMidX, motherCoupleMidX;

    if (stepFather) {
      // [Father]—❤—[Mother]—❤—[StepFather]
      // You connect from father+mother couple, half-siblings from mother+stepFather couple
      // Position so fatherCoupleMidX (your parents) aligns with centerX (you)
      fatherCoupleMidX = centerX; // you are directly below your parents
      fatherX = fatherCoupleMidX - GAP_COUPLE / 2 - CARD_W / 2;
      motherX = fatherCoupleMidX + GAP_COUPLE / 2 + CARD_W / 2;
      stepFatherX = motherX + CARD_W / 2 + GAP_COUPLE + CARD_W / 2;
      motherCoupleMidX = (motherX + stepFatherX) / 2;
    } else if (stepMother) {
      // [StepMother]—❤—[Father]—❤—[Mother]
      // You connect from father+mother couple, half-siblings from father+stepMother couple
      // Position so motherCoupleMidX (your parents) aligns with centerX (you)
      motherCoupleMidX = centerX; // you are directly below your parents
      motherX = motherCoupleMidX + GAP_COUPLE / 2 + CARD_W / 2;
      fatherX = motherCoupleMidX - GAP_COUPLE / 2 - CARD_W / 2;
      stepMotherX = fatherX - CARD_W / 2 - GAP_COUPLE - CARD_W / 2;
      fatherCoupleMidX = (fatherX + stepMotherX) / 2;
    } else {
      // Traditional: father + mother as couple
      fatherX = centerX - GAP_COUPLE / 2 - CARD_W / 2;
      motherX = centerX + GAP_COUPLE / 2 + CARD_W / 2;
      fatherCoupleMidX = (fatherX + motherX) / 2;
      motherCoupleMidX = fatherCoupleMidX;
    }

    // Position father-side siblings centered above their cousin groups
    let fSibCursor = fatherX - GAP_X;
    for (let i = fatherSibData.length - 1; i >= 0; i--) {
      const d = fatherSibData[i];
      const children = d.sib.children || [];
      if (children.length > 0) {
        // Find this sibling's cousin group position
        const fcGroup = level0GroupPositions.find(g => g.type === 'fcousin' && g.index === fatherCousinGroups.indexOf(d));
        if (fcGroup) {
          const groupCenterX = (fcGroup.startX + fcGroup.endX) / 2 + shiftX;
          d.parentMidX = groupCenterX;
          const coupleW = coupleWidth(d.sib);
          d.sibX = groupCenterX - coupleW / 2 + (d.spouse ? CARD_W / 2 : 0);
          d.spouseX = d.spouse ? d.sibX + CARD_W + GAP_COUPLE : null;
        }
      } else {
        // No children — place to the left of the previous sibling
        const unitW = coupleWidth(d.sib);
        d.sibX = fSibCursor - unitW + CARD_W / 2;
        d.spouseX = d.spouse ? d.sibX + CARD_W + GAP_COUPLE : null;
        d.parentMidX = d.spouse ? (d.sibX + d.spouseX) / 2 : d.sibX;
        fSibCursor = d.sibX - CARD_W / 2 - GAP_X;
      }
    }

    // Position father-side siblings: couple midpoint (heart) centered above children
    fatherSibData.forEach(d => {
      if (d.childData.length > 0) {
        const childMidX = (d.childData[0].childX + d.childData[d.childData.length - 1].childX) / 2;
        if (d.spouse) {
          // Couple: heart at childMidX, uncle left, aunt right
          d.sibX = childMidX - GAP_COUPLE / 2 - CARD_W / 2 + GAP_COUPLE / 2 + CARD_W / 2;
          // Simplify: sibX = childMidX - (GAP_COUPLE + CARD_W) / 2
          d.sibX = childMidX - (GAP_COUPLE + CARD_W) / 2;
          d.spouseX = d.sibX + CARD_W + GAP_COUPLE;
          d.parentMidX = (d.sibX + d.spouseX) / 2; // = childMidX
        } else {
          d.sibX = childMidX;
          d.parentMidX = childMidX;
        }
      }
    });

    // Ensure father-side siblings don't overlap with father
    // Check that the rightmost father-side sib is left of father with a gap
    const fatherSibRightEdge = fatherSibData.length > 0
      ? Math.max(...fatherSibData.map(d => d.spouseX ? d.spouseX : d.sibX)) + CARD_W / 2
      : -Infinity;
    if (fatherSibRightEdge >= fatherX - GAP_X) {
      const shift = fatherSibRightEdge + GAP_X - (fatherX - CARD_W / 2);
      fatherSibData.forEach(d => { d.sibX -= shift; if (d.spouseX) d.spouseX -= shift; d.parentMidX -= shift; d.childData.forEach(c => { c.childX -= shift; }); });
      fatherCousinData.forEach(d => { d.childX -= shift; d.parentMidX -= shift; });
    }

    // Position mother-side siblings: couple midpoint (heart) centered above children
    let mSibCursor = motherX + GAP_X;
    motherSibData.forEach((d, i) => {
      const children = d.sib.children || [];
      if (children.length > 0) {
        const childMidX = (d.childData[0].childX + d.childData[d.childData.length - 1].childX) / 2;
        if (d.spouse) {
          d.sibX = childMidX - (GAP_COUPLE + CARD_W) / 2;
          d.spouseX = d.sibX + CARD_W + GAP_COUPLE;
          d.parentMidX = (d.sibX + d.spouseX) / 2; // = childMidX
        } else {
          d.sibX = childMidX;
          d.parentMidX = childMidX;
        }
      } else {
        const unitW = coupleWidth(d.sib);
        d.sibX = mSibCursor + CARD_W / 2;
        d.spouseX = d.spouse ? d.sibX + CARD_W + GAP_COUPLE : null;
        d.parentMidX = d.spouse ? (d.sibX + d.spouseX) / 2 : d.sibX;
        mSibCursor = (d.spouseX ? d.spouseX : d.sibX) + CARD_W / 2 + GAP_X;
      }
    });

    // Ensure mother-side siblings don't overlap with mother
    const motherSibLeftEdge = motherSibData.length > 0
      ? Math.min(...motherSibData.map(d => d.sibX)) - CARD_W / 2
      : Infinity;
    if (motherSibLeftEdge <= motherX + GAP_X) {
      const shift = (motherX + CARD_W / 2 + GAP_X) - motherSibLeftEdge;
      motherSibData.forEach(d => { d.sibX += shift; if (d.spouseX) d.spouseX += shift; d.parentMidX += shift; d.childData.forEach(c => { c.childX += shift; }); });
      motherCousinData.forEach(d => { d.childX += shift; d.parentMidX += shift; });
    }

    // ── Resolve overlaps between Level 1 sibling units ──
    // Father-side: sort left to right, push apart if overlapping
    function resolveLevel1Overlaps(sibData) {
      if (sibData.length <= 1) return;
      // Sort by leftmost edge
      const sorted = [...sibData].sort((a, b) => {
        const aLeft = Math.min(a.sibX, a.spouseX || a.sibX) - CARD_W / 2;
        const bLeft = Math.min(b.sibX, b.spouseX || b.sibX) - CARD_W / 2;
        return aLeft - bLeft;
      });
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];
        const prevRight = Math.max(prev.sibX, prev.spouseX || prev.sibX) + CARD_W / 2;
        const currLeft = Math.min(curr.sibX, curr.spouseX || curr.sibX) - CARD_W / 2;
        if (currLeft < prevRight + GAP_X) {
          const shift = prevRight + GAP_X - currLeft;
          curr.sibX += shift;
          if (curr.spouseX) curr.spouseX += shift;
          curr.parentMidX += shift;
          curr.childData.forEach(c => { c.childX += shift; });
        }
      }
    }
    resolveLevel1Overlaps(fatherSibData);
    resolveLevel1Overlaps(motherSibData);

    // Re-check father-side vs father overlap after sibling resolution
    const fatherSibRightEdge2 = fatherSibData.length > 0
      ? Math.max(...fatherSibData.map(d => d.spouseX ? d.spouseX : d.sibX)) + CARD_W / 2
      : -Infinity;
    if (fatherSibRightEdge2 >= fatherX - GAP_X) {
      const shift = fatherSibRightEdge2 + GAP_X - (fatherX - CARD_W / 2);
      fatherSibData.forEach(d => { d.sibX -= shift; if (d.spouseX) d.spouseX -= shift; d.parentMidX -= shift; d.childData.forEach(c => { c.childX -= shift; }); });
      fatherCousinData.forEach(d => { d.childX -= shift; d.parentMidX -= shift; });
    }

    // Re-check mother-side vs mother overlap after sibling resolution
    const motherSibLeftEdge2 = motherSibData.length > 0
      ? Math.min(...motherSibData.map(d => d.sibX)) - CARD_W / 2
      : Infinity;
    if (motherSibLeftEdge2 <= motherX + CARD_W / 2 + GAP_X) {
      const shift = (motherX + CARD_W / 2 + GAP_X) - motherSibLeftEdge2;
      motherSibData.forEach(d => { d.sibX += shift; if (d.spouseX) d.spouseX += shift; d.parentMidX += shift; d.childData.forEach(c => { c.childX += shift; }); });
      motherCousinData.forEach(d => { d.childX += shift; d.parentMidX += shift; });
    }

    // ── Step 4: Position Level 2 (grandparents) centered above Level 1 ──
    const allPaternalChildrenXs = [
      ...fatherSibData.flatMap(d => d.spouse ? [d.sibX, d.spouseX] : [d.sibX]),
      fatherX,
    ];
    const paternalMidX = (Math.min(...allPaternalChildrenXs) + Math.max(...allPaternalChildrenXs)) / 2;
    const pgfX = paternalMidX - GAP_COUPLE / 2 - CARD_W / 2;
    const pgmX = paternalMidX + GAP_COUPLE / 2 + CARD_W / 2;

    const allMaternalChildrenXs = [
      motherX,
      ...motherSibData.flatMap(d => d.spouse ? [d.sibX, d.spouseX] : [d.sibX]),
    ];
    const maternalMidX = (Math.min(...allMaternalChildrenXs) + Math.max(...allMaternalChildrenXs)) / 2;
    let mgfX = maternalMidX - GAP_COUPLE / 2 - CARD_W / 2;
    let mgmX = maternalMidX + GAP_COUPLE / 2 + CARD_W / 2;

    // Enforce minimum gap between grandparent couples to prevent overlap
    const minGPGap = GAP_X;
    const pgmRightEdge = pgmX + CARD_W / 2;
    const mgfLeftEdge = mgfX - CARD_W / 2;
    if (mgfLeftEdge < pgmRightEdge + minGPGap) {
      const shift = (pgmRightEdge + minGPGap) - mgfLeftEdge;
      mgfX += shift;
      mgmX += shift;
    }

    // ── Step 5: Position Level -1 groups (collapsible children) ──
    const rootChildData = [];
    if (rootChildren.length > 0) {
      const rootCoupleMidX = root.spouse && rootSpouseX ? (centerX + rootSpouseX) / 2 : centerX;
      const startCX = rootCoupleMidX - ((rootChildren.length - 1) * (CARD_W + GAP_X)) / 2;
      for (let i = 0; i < rootChildren.length; i++) {
        rootChildData.push({ child: rootChildren[i], childX: startCX + i * (CARD_W + GAP_X) });
      }
    }

    // Build Level -1 groups for collapse tracking
    const levelMinus1Groups = [];
    if (rootChildData.length > 0) {
      const rootCoupleMidX = root.spouse ? (centerX + rootSpouseX) / 2 : centerX;
      levelMinus1Groups.push({ type: 'root', childData: rootChildData, parentMidX: rootCoupleMidX });
    }
    genRowData.forEach(d => {
      if (d.childData && d.childData.length > 0) {
        levelMinus1Groups.push({ type: 'sibling', childData: d.childData, parentMidX: d.parentMidX });
      }
    });
    levelMinus1Groups.forEach((group, gi) => { group.id = `lminus1-${gi}`; });

    const expandedGroups = levelMinus1Groups.filter(g => !collapsedGroups.has(g.id));
    // Position expanded children centered under their parent
    expandedGroups.forEach(group => {
      const startCX = group.parentMidX - ((group.childData.length - 1) * (CARD_W + GAP_X)) / 2;
      group.childData.forEach((cd, ci) => {
        cd.childX = startCX + ci * (CARD_W + GAP_X);
      });
    });
    // No overlap resolution needed — only one group can be expanded at a time

    const svgEl = d3.select(containerRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('touch-action', 'none')
      .style('cursor', 'grab');
    const g = svgEl.append('g');

    const zoom = d3.zoom()
      .scaleExtent([0.2, 4])
      .on('start', () => { svgEl.style('cursor', 'grabbing'); })
      .on('end', () => { svgEl.style('cursor', 'grab'); })
      .on('zoom', (event) => { g.attr('transform', event.transform); });
    svgEl.call(zoom);

    const totalH = levelMinus1Y + CARD_H / 2 + 20;
    const allNodeXs = [
      ...fatherSibData.flatMap(d => d.spouse ? [d.sibX, d.spouseX] : [d.sibX]),
      fatherX, motherX,
      ...motherSibData.flatMap(d => d.spouse ? [d.sibX, d.spouseX] : [d.sibX]),
      pgfX, pgmX, mgfX, mgmX,
      centerX, ...genRowData.flatMap(d => d.spouseX ? [d.sibX, d.spouseX] : [d.sibX]),
      ...(rootSpouseX ? [rootSpouseX] : []),
      ...rootChildData.map(d => d.childX),
      ...genRowData.flatMap(d => d.childData ? d.childData.map(c => c.childX) : []),
      ...fatherCousinData.map(d => d.childX),
      ...paternalHalfData.filter(d => d.childX).map(d => d.childX),
      ...motherCousinData.map(d => d.childX),
      ...maternalHalfData.filter(d => d.childX).map(d => d.childX),
    ];
    const treeMinX = Math.min(...allNodeXs) - CARD_W / 2;
    const treeMaxX = Math.max(...allNodeXs) + CARD_W / 2;
    const initialTransform = d3.zoomIdentity
      .translate(width / 2 - (treeMinX + treeMaxX) / 2, height / 2 - totalH / 2)
      .scale(1);
    // Restore zoom/pan if available, otherwise use initial transform
    if (savedTransform.current) {
      svgEl.call(zoom.transform, savedTransform.current);
    } else {
      svgEl.call(zoom.transform, initialTransform);
    }

    const tooltipEntries = [];

    // ── Determine active root ──
    const isActive = (person) => person.id === activePersonId;
    const drawCtx = { isActive, query, selectedLanguage, activePersonId, onViewFamily, onNodeClick, tooltipEntries };


    // ── Draw Level 2: Grandparent couples ──
    drawPersonCard(g, grandparentGeneration.paternalGrandfather, pgfX, level2Y, drawCtx);
    drawPersonCard(g, grandparentGeneration.paternalGrandmother, pgmX, level2Y, drawCtx);
    drawCoupleConnector(g, pgfX, pgmX, level2Y);

    drawPersonCard(g, grandparentGeneration.maternalGrandfather, mgfX, level2Y, drawCtx);
    drawPersonCard(g, grandparentGeneration.maternalGrandmother, mgmX, level2Y, drawCtx);
    drawCoupleConnector(g, mgfX, mgmX, level2Y);

    // Draw step-grandparents if present
    if (stepFather && grandparentGeneration.stepPaternalGrandfather) {
      const spgfX = pgfX - CARD_W - GAP_X - CARD_W;
      const spgmX = pgfX - CARD_W - GAP_X;
      drawPersonCard(g, grandparentGeneration.stepPaternalGrandfather, spgfX, level2Y, drawCtx);
      drawPersonCard(g, grandparentGeneration.stepPaternalGrandmother, spgmX, level2Y, drawCtx);
      drawCoupleConnector(g, spgfX, spgmX, level2Y);
    }
    if (stepMother && grandparentGeneration.stepMaternalGrandfather) {
      const smgfX = mgmX + CARD_W / 2 + GAP_X;
      const smgmX = smgfX + CARD_W + GAP_COUPLE;
      drawPersonCard(g, grandparentGeneration.stepMaternalGrandfather, smgfX, level2Y, drawCtx);
      drawPersonCard(g, grandparentGeneration.stepMaternalGrandmother, smgmX, level2Y, drawCtx);
      drawCoupleConnector(g, smgfX, smgmX, level2Y);
    }

    // ── Draw Level 1: Parents + Step-Parents + Siblings + Spouses ──
    drawPersonCard(g, father, fatherX, level1Y, drawCtx);
    drawPersonCard(g, mother, motherX, level1Y, drawCtx);

    if (stepFather) {
      drawPersonCard(g, stepFather, stepFatherX, level1Y, drawCtx);
      drawCoupleConnector(g, fatherX, motherX, level1Y); // biological parents
      drawCoupleConnector(g, motherX, stepFatherX, level1Y); // mother + step-father
    } else if (stepMother) {
      drawPersonCard(g, stepMother, stepMotherX, level1Y, drawCtx);
      drawCoupleConnector(g, stepMotherX, fatherX, level1Y); // step-mother + father
      drawCoupleConnector(g, fatherX, motherX, level1Y); // biological parents
    } else {
      drawCoupleConnector(g, fatherX, motherX, level1Y);
    }

    fatherSibData.forEach(({ sib, sibX, spouse, spouseX }) => {
      drawPersonCard(g, sib, sibX, level1Y, drawCtx);
      if (spouse && spouseX != null) {
        drawPersonCard(g, spouse, spouseX, level1Y, drawCtx);
        drawCoupleConnector(g, sibX, spouseX, level1Y);
      }
    });

    motherSibData.forEach(({ sib, sibX, spouse, spouseX }) => {
      drawPersonCard(g, sib, sibX, level1Y, drawCtx);
      if (spouse && spouseX != null) {
        drawPersonCard(g, spouse, spouseX, level1Y, drawCtx);
        drawCoupleConnector(g, sibX, spouseX, level1Y);
      }
    });

    // ── Draw Level 0: Generation row (root + siblings) + Cousins ──
    genRowData.forEach(({ person, sibX, spouseX }) => {
      drawPersonCard(g, person, sibX, level0Y, drawCtx);
      if (person.spouse && spouseX != null) {
        drawPersonCard(g, person.spouse, spouseX, level0Y, drawCtx);
        drawCoupleConnector(g, sibX, spouseX, level0Y);
      }
    });
    // Draw first cousins
    fatherCousinData.forEach(({ child, childX }) => {
      drawPersonCard(g, child, childX, level0Y, drawCtx);
    });
    // Draw paternal half-siblings
    paternalHalfData.forEach(({ person, childX }) => {
      if (childX) drawPersonCard(g, person, childX, level0Y, drawCtx);
    });
    motherCousinData.forEach(({ child, childX }) => {
      drawPersonCard(g, child, childX, level0Y, drawCtx);
    });
    // Draw maternal half-siblings
    maternalHalfData.forEach(({ person, childX }) => {
      if (childX) drawPersonCard(g, person, childX, level0Y, drawCtx);
    });

    // ── Draw Level -1: Children (use pre-computed groups) ──

    // Draw expanded children
    expandedGroups.forEach(group => {
      group.childData.forEach(cd => {
        drawPersonCard(g, cd.child, cd.childX, levelMinus1Y, drawCtx);
      });
    });

    // Draw +N children badges on each couple that has kids
    // Only one group can be expanded at a time — clicking expands one and collapses all others
    const toggleGroup = (groupId) => {
      setCollapsedGroups(prev => {
        if (prev.has(groupId)) {
          // Group is collapsed — expand it (and only it)
          const totalGroups = (rootChildData.length > 0 ? 1 : 0) + genRowData.filter(d => d.childData && d.childData.length > 0).length;
          const allCollapsed = new Set();
          for (let i = 0; i < totalGroups; i++) allCollapsed.add(`lminus1-${i}`);
          allCollapsed.delete(groupId); // only this group is expanded
          return allCollapsed;
        } else {
          // Group is expanded — collapse it (collapse all)
          const totalGroups = (rootChildData.length > 0 ? 1 : 0) + genRowData.filter(d => d.childData && d.childData.length > 0).length;
          const allCollapsed = new Set();
          for (let i = 0; i < totalGroups; i++) allCollapsed.add(`lminus1-${i}`);
          return allCollapsed;
        }
      });
    };

    levelMinus1Groups.forEach(group => {
      const isCollapsed = collapsedGroups.has(group.id);
      const n = group.childData.length;
      // Draw badge near the heart/couple midpoint
      const badgeX = group.parentMidX;
      const badgeY = level0Y + CARD_H / 2 + 6;
      const badgeG = g.append('g')
        .attr('class', 'children-badge')
        .attr('transform', `translate(${badgeX}, ${badgeY})`)
        .style('cursor', 'pointer')
        .on('click', (event) => {
          event.stopPropagation();
          toggleGroup(group.id);
        });

      // Badge background
      const badgeW = isCollapsed ? 60 : 50;
      badgeG.append('rect')
        .attr('x', -badgeW / 2).attr('y', 0)
        .attr('width', badgeW).attr('height', 20)
        .attr('rx', 10)
        .attr('fill', isCollapsed ? '#6366f1' : '#e5e7eb')
        .attr('stroke', isCollapsed ? '#4f46e5' : '#d1d5db')
        .attr('stroke-width', 1);

      // Badge text
      badgeG.append('text')
        .attr('x', 0).attr('y', 14)
        .attr('text-anchor', 'middle')
        .attr('fill', isCollapsed ? '#fff' : '#374151')
        .attr('font-size', '10px').attr('font-weight', '600')
        .attr('style', 'pointer-events: none')
        .text(isCollapsed ? `+${n} kids` : '\u2212');
    });

    // ── Connector lines ──
    // Lines start from the heart between couples, not from bottom of cards
    const heartDropY = 12; // pixels below the couple line where the vertical drop starts
    const sibBarY = level2Y + heartDropY + (level1Y - level2Y - heartDropY) / 2;

    // Helper: draw a connector line
    function drawLine(x1, y1, x2, y2, _active = false) {
      g.append('line')
        .attr('x1', x1).attr('y1', y1)
        .attr('x2', x2).attr('y2', y2)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1.5);
    }

    // Paternal grandparents → Father + siblings (blood relatives only)
    // Step-father's siblings are NOT connected to paternal grandparents
    const paternalBloodXs = [
      ...fatherSiblings.map((sib, i) => fatherSibData.find(d => d.sib.id === sib.id)?.sibX).filter(x => x != null),
      fatherX,
    ];
    if (paternalBloodXs.length > 0) {
      const paternalGpMidX = (pgfX + pgmX) / 2;
      drawLine(paternalGpMidX, level2Y + heartDropY, paternalGpMidX, sibBarY, true);
      const barLeft = Math.min(...paternalBloodXs);
      const barRight = Math.max(...paternalBloodXs);
      if (barRight > barLeft) {
        g.append('line')
          .attr('x1', barLeft).attr('y1', sibBarY)
          .attr('x2', barRight).attr('y2', sibBarY)
          .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
      }
      paternalBloodXs.forEach(cx => {
        drawLine(cx, sibBarY, cx, level1Y - CARD_H / 2, cx === fatherX);
      });
    }

    // Maternal grandparents → Mother + siblings (blood relatives only)
    // Step-mother's siblings are NOT connected to maternal grandparents
    const maternalBloodXs = [
      motherX,
      ...motherSiblings.map((sib, i) => motherSibData.find(d => d.sib.id === sib.id)?.sibX).filter(x => x != null),
    ];
    if (maternalBloodXs.length > 0) {
      const maternalGpMidX = (mgfX + mgmX) / 2;
      drawLine(maternalGpMidX, level2Y + heartDropY, maternalGpMidX, sibBarY, true);
      const barLeft = Math.min(...maternalBloodXs);
      const barRight = Math.max(...maternalBloodXs);
      if (barRight > barLeft) {
        g.append('line')
          .attr('x1', barLeft).attr('y1', sibBarY)
          .attr('x2', barRight).attr('y2', sibBarY)
          .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
      }
      maternalBloodXs.forEach(cx => {
        drawLine(cx, sibBarY, cx, level1Y - CARD_H / 2, cx === motherX);
      });
    }
    // (Spouse couple connectors are already drawn by drawCoupleConnector above)

    // ── Parent → Child connectors ──
    // For step-parent layouts, you connect from your biological parents' couple midpoint,
    // and half-siblings connect from their respective parent couple midpoint.
    const childBarY = level1Y + heartDropY + (level0Y - level1Y - heartDropY) / 2;

    if (stepFather) {
      // [Father]—❤—[Mother]—❤—[StepFather]
      // You + siblings connect from father+mother couple midpoint (straight down)
      drawLine(fatherCoupleMidX, level1Y + heartDropY, fatherCoupleMidX, childBarY, true);
      // Half-siblings (maternal) connect from mother+stepFather couple midpoint (straight down)
      if (maternalHalfData.length > 0 && maternalHalfPositions.length > 0) {
        const maternalHalfXs = maternalHalfData.map(d => d.childX).filter(x => x);
        if (maternalHalfXs.length > 0) {
          const halfBarLeft = Math.min(...maternalHalfXs);
          const halfBarRight = Math.max(...maternalHalfXs);
          drawLine(motherCoupleMidX, level1Y + heartDropY, motherCoupleMidX, childBarY, true);
          // Horizontal bar from first to last half-sibling
          g.append('line')
            .attr('x1', halfBarLeft).attr('y1', childBarY)
            .attr('x2', halfBarRight).attr('y2', childBarY)
            .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          // Connect parent drop to bar if it's outside the bar range
          if (motherCoupleMidX < halfBarLeft) {
            g.append('line').attr('x1', motherCoupleMidX).attr('y1', childBarY).attr('x2', halfBarLeft).attr('y2', childBarY).attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          } else if (motherCoupleMidX > halfBarRight) {
            g.append('line').attr('x1', halfBarRight).attr('y1', childBarY).attr('x2', motherCoupleMidX).attr('y2', childBarY).attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          }
          // Vertical drops to each half-sibling
          maternalHalfData.forEach(({ childX }) => {
            if (childX) drawLine(childX, childBarY, childX, level0Y - CARD_H / 2, false);
          });
        }
      }
    } else if (stepMother) {
      // [StepMother]—❤—[Father]—❤—[Mother]
      // You + siblings connect from father+mother couple midpoint (straight down)
      drawLine(motherCoupleMidX, level1Y + heartDropY, motherCoupleMidX, childBarY, true);
      // Half-siblings (paternal) connect from father+stepMother couple midpoint (straight down)
      if (paternalHalfData.length > 0 && paternalHalfPositions.length > 0) {
        const paternalHalfXs = paternalHalfData.map(d => d.childX).filter(x => x);
        if (paternalHalfXs.length > 0) {
          const halfBarLeft = Math.min(...paternalHalfXs);
          const halfBarRight = Math.max(...paternalHalfXs);
          drawLine(fatherCoupleMidX, level1Y + heartDropY, fatherCoupleMidX, childBarY, true);
          // Horizontal bar from first to last half-sibling
          g.append('line')
            .attr('x1', halfBarLeft).attr('y1', childBarY)
            .attr('x2', halfBarRight).attr('y2', childBarY)
            .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          // Connect parent drop to bar if it's outside the bar range
          if (fatherCoupleMidX < halfBarLeft) {
            g.append('line').attr('x1', fatherCoupleMidX).attr('y1', childBarY).attr('x2', halfBarLeft).attr('y2', childBarY).attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          } else if (fatherCoupleMidX > halfBarRight) {
            g.append('line').attr('x1', halfBarRight).attr('y1', childBarY).attr('x2', fatherCoupleMidX).attr('y2', childBarY).attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          }
          // Vertical drops to each half-sibling
          paternalHalfData.forEach(({ childX }) => {
            if (childX) drawLine(childX, childBarY, childX, level0Y - CARD_H / 2, false);
          });
        }
      }
    } else {
      // Traditional: single couple line down to children
      const parentMidX = (fatherX + motherX) / 2;
      drawLine(parentMidX, level1Y + heartDropY, parentMidX, childBarY, true);
    }

    // Child connectors: horizontal bar from first to last sibling,
    // then vertical drops to each person card
    const allDropXs = genRowData.flatMap(d => d.spouseX ? [d.sibX, d.spouseX] : [d.sibX]);
    const personXs = genRowData.map(d => d.sibX);
    if (personXs.length > 1) {
      // Horizontal bar from first to last sibling only
      const barLeft = Math.min(...personXs);
      const barRight = Math.max(...personXs);
      g.append('line')
        .attr('x1', barLeft).attr('y1', childBarY)
        .attr('x2', barRight).attr('y2', childBarY)
        .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
      // Connect parent drop to bar if needed
      const parentDropX = stepFather ? fatherCoupleMidX : (stepMother ? motherCoupleMidX : (fatherX + motherX) / 2);
      if (parentDropX < barLeft) {
        g.append('line').attr('x1', parentDropX).attr('y1', childBarY).attr('x2', barLeft).attr('y2', childBarY).attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
      } else if (parentDropX > barRight) {
        g.append('line').attr('x1', barRight).attr('y1', childBarY).attr('x2', parentDropX).attr('y2', childBarY).attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
      }
    }
    // Vertical drops to each person card
    allDropXs.forEach(cx => {
      drawLine(cx, childBarY, cx, level0Y - CARD_H / 2, cx === centerX);
    });

    // Child bar for half-siblings is handled above in the step-parent section
    // (vertical drops from parent couple to child bar to each half-sibling)

    // Aunt/Uncle couples → Cousins
    // Use a slightly different Y position than childBarY to avoid overlapping with the sibling bar
    const cousinBarY = childBarY;
    fatherSibData.forEach(({ sibX, spouseX, childData, parentMidX }) => {
      if (childData && childData.length > 0) {
        // Vertical drop from parent midpoint to bar
        g.append('line')
          .attr('x1', parentMidX).attr('y1', level1Y + heartDropY)
          .attr('x2', parentMidX).attr('y2', cousinBarY)
          .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
        // Vertical drops to each child — no horizontal bar for cousins
        // (they connect directly via the parent drop line)
        childData.forEach(({ childX }) => {
          if (childX) {
            g.append('line')
              .attr('x1', parentMidX).attr('y1', cousinBarY)
              .attr('x2', childX).attr('y2', cousinBarY)
              .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
            g.append('line')
              .attr('x1', childX).attr('y1', cousinBarY)
              .attr('x2', childX).attr('y2', level0Y - CARD_H / 2)
              .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          }
        });
      }
    });
    motherSibData.forEach(({ sibX, spouseX, childData, parentMidX }) => {
      if (childData && childData.length > 0) {
        g.append('line')
          .attr('x1', parentMidX).attr('y1', level1Y + heartDropY)
          .attr('x2', parentMidX).attr('y2', cousinBarY)
          .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
        childData.forEach(({ childX }) => {
          if (childX) {
            g.append('line')
              .attr('x1', parentMidX).attr('y1', cousinBarY)
              .attr('x2', childX).attr('y2', cousinBarY)
              .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
            g.append('line')
              .attr('x1', childX).attr('y1', cousinBarY)
              .attr('x2', childX).attr('y2', level0Y - CARD_H / 2)
              .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          }
        });
      }
    });

    // Level 0 → Level -1 connectors (heart drop pattern, only for expanded groups)
    if (anyLevelMinus1Expanded) {
      const descBarY = level0Y + heartDropY + (levelMinus1Y - level0Y - heartDropY) / 2;
      expandedGroups.forEach(group => {
        if (group.childData.length > 0) {
          g.append('line')
            .attr('x1', group.parentMidX).attr('y1', level0Y + heartDropY)
            .attr('x2', group.parentMidX).attr('y2', descBarY)
            .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          const childXs = group.childData.map(c => c.childX);
          g.append('line')
            .attr('x1', Math.min(...childXs)).attr('y1', descBarY)
            .attr('x2', Math.max(...childXs)).attr('y2', descBarY)
            .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          childXs.forEach(cx => {
            g.append('line')
              .attr('x1', cx).attr('y1', descBarY)
              .attr('x2', cx).attr('y2', levelMinus1Y - CARD_H / 2)
              .attr('stroke', '#d1d5db').attr('stroke-width', 1.5);
          });
        }
      });
    }

    // ── Tooltip overlay (rendered LAST so on top of everything) ──
    const tooltipOverlay = g.append('g').attr('class', 'tooltip-overlay');
    tooltipEntries.forEach(({ person, x, y }) => {
      const tooltipG = tooltipOverlay.append('g')
        .attr('class', `tooltip-${person.id}`)
        .attr('transform', `translate(${x}, ${y})`)
        .attr('opacity', 0)
        .style('pointer-events', 'none');

      const tooltipW = 180;
      const tooltipH = 64;
      const tooltipY = -CARD_H / 2 - tooltipH - 8;

      tooltipG.append('rect')
        .attr('x', -tooltipW / 2).attr('y', tooltipY)
        .attr('width', tooltipW).attr('height', tooltipH)
        .attr('rx', 8)
        .attr('fill', '#1f2937')
        .attr('opacity', 0.92);

      tooltipG.append('polygon')
        .attr('points', `${-6},${tooltipY + tooltipH} ${0},${tooltipY + tooltipH + 6} ${6},${tooltipY + tooltipH}`)
        .attr('fill', '#1f2937')
        .attr('opacity', 0.92);

      tooltipG.append('text')
        .attr('x', 0).attr('y', tooltipY + 20)
        .attr('text-anchor', 'middle')
        .attr('fill', '#ffffff').attr('font-size', '13px').attr('font-weight', '600')
        .text(person.name || '');

      const detail = person.isLiving !== false
        ? (person.gender === 'MALE' ? 'Male' : person.gender === 'FEMALE' ? 'Female' : '')
        : 'Deceased';
      const tooltipTerm = person.kinshipTerm ? getTranslatedTerm(person.kinshipTerm, selectedLanguage) : null;
      tooltipG.append('text')
        .attr('x', 0).attr('y', tooltipY + 38)
        .attr('text-anchor', 'middle')
        .attr('fill', '#d1d5db').attr('font-size', '11px')
        .text(tooltipTerm ? `${detail} · ${tooltipTerm}` : detail);

      // "View Family" indicator in tooltip
      if (person.family) {
        tooltipG.append('text')
          .attr('x', 0).attr('y', tooltipY + 54)
          .attr('text-anchor', 'middle')
          .attr('fill', '#a5b4fc').attr('font-size', '10px').attr('font-weight', '500')
          .text('Tap to view family →');
      }
    });

    // Store zoom refs
    zoomRef.current = zoom;
    initialRef.current = initialTransform;
    containerRef.current.__svgEl = svgEl.node();

  }, [pedigreeData, dimensions, onNodeClick, onViewFamily, query, selectedLanguage, activePersonId, collapsedGroups]);

  useImperativeHandle(ref, () => ({
    zoomIn: () => {
      const svgNode = containerRef.current?.__svgEl;
      const zoom = zoomRef.current;
      if (svgNode && zoom) d3.select(svgNode).transition().duration(300).call(zoom.scaleBy, 1.3);
    },
    zoomOut: () => {
      const svgNode = containerRef.current?.__svgEl;
      const zoom = zoomRef.current;
      if (svgNode && zoom) d3.select(svgNode).transition().duration(300).call(zoom.scaleBy, 0.7);
    },
    resetView: () => {
      const svgNode = containerRef.current?.__svgEl;
      const zoom = zoomRef.current;
      const initial = initialRef.current;
      if (svgNode && zoom && initial) d3.select(svgNode).transition().duration(500).call(zoom.transform, initial);
    },
  }), [dimensions]);

  return (
    <div ref={containerRef} className="w-full h-full relative" />
  );
});
export default FamilyTree;
